import React, { useEffect, useMemo, useRef } from 'react';
import Button from '../components/Inputs/Button/Button';
import ModalShell from '../components/ModalShell/ModalShell';
import styles from '../components/ModalShell/ModalShell.module.css';
import Field from '../components/Field';
import useOptionsMap from '../hooks/useOptionsMap';


function firstEnabled(opts = []) {
    return (opts.find(o => !o.disabled) || opts[0]);
}


function coerceFormattedValue(f, raw) {
    if (typeof f?.format === 'function') return f.format(raw);
    return raw;
}


function coerceParsedValue(f, val) {
    if (typeof f?.parse === 'function') return f.parse(val);
    return val;
}


export default function ModalContainer({
    open, mode = 'create', title, subtitle, fields = [],
    values, setValue, onNo, onYes,
    yesLabel = mode === 'delete' ? 'Delete' : 'Save',
    noLabel = 'Cancel',
    yesVariant = mode === 'delete' ? 'danger' : 'primary',
    size = 'md',
}) {
    const NULL_OPTION = useMemo(() => ({ value: null, label: 'None' }), []);

    // Include 'multi' so its options get loaded just like select/radio
    const optionDescriptors = useMemo(() => (
        (fields || [])
            .filter(f => ['select', 'radio', 'multi'].includes(f.type))
            .map(f => {
                const isSelectLike = f.type === 'select';
                const injectNull = isSelectLike && !f.required;
                const nullOpt = f.nullOption ?? NULL_OPTION;

                // static options
                const opts = Array.isArray(f.options)
                    ? (injectNull ? [nullOpt, ...f.options] : f.options)
                    : undefined;

                // async options
                const loadOptions = typeof f.loadOptions === 'function'
                    ? async () => {
                        const arr = await f.loadOptions();
                        const list = Array.isArray(arr) ? arr : [];
                        return injectNull ? [nullOpt, ...list] : list;
                    }
                    : undefined;

                return { key: f.key, options: opts, loadOptions };
            })
    ), [fields, NULL_OPTION]);


    const optsMap = useOptionsMap(optionDescriptors);


    // one-shot initialize defaults for selects/radios when modal opens
    const initializedRef = useRef(false);
    useEffect(() => {
        if (!open) { initializedRef.current = false; return; }
        if (initializedRef.current) return;


        (fields || []).forEach((f) => {
            if (!['select', 'radio'].includes(f.type)) return;

            const v = values?.[f.key];
            if (v !== undefined && v !== null && String(v) !== '') return;


            const opts = optsMap[f.key] || [];
            const def = firstEnabled(opts);
            if (def && (f.autoDefault ?? true)) setValue(f.key, def.value);
        });


        initializedRef.current = true;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, fields, optsMap, values]);

    const requiredOK = useMemo(() => (
        (fields || [])
            .filter(f => f.required)
            .every(f => {
                let v = values?.[f.key];

                if (['select', 'radio'].includes(f.type)) {
                    if ((v === undefined || v === null || String(v) === '')) {
                        const def = firstEnabled(optsMap[f.key] || []);
                        v = (f.autoDefault ?? true) ? def?.value : v;
                    }
                    return v !== undefined && v !== null && String(v).trim() !== '';
                }

                if (f.type === 'multi') {
                    const arr = Array.isArray(v) ? v : [];
                    if (arr.length === 0) return false;

                    // each row must have type & value
                    const filled = arr.every(it =>
                        it &&
                        it.type !== undefined && it.type !== null && String(it.type).trim() !== '' &&
                        it.value !== undefined && it.value !== null && String(it.value).trim() !== ''
                    );
                    if (!filled) return false;

                    // enforce uniqueness of types
                    const types = arr.map(it => it.type);
                    const unique = new Set(types).size === types.length;
                    return unique;
                }

                // inputs/date/etc.
                return v !== undefined && v !== null && String(v).trim() !== '';
            })
    ), [fields, values, optsMap]);


    const header = (
        <div className={styles.header}>
            <div><h2 className={styles.title}>{title}</h2></div>
            <Button variant="secondary" shape="square" aria-label="Close" onClick={onNo}>×</Button>
        </div>
    );


    const footer = (
        <div className={styles.footer}>
            <Button variant="secondary" shape="pill" onClick={onNo}>{noLabel}</Button>
            <Button variant={yesVariant} shape="pill" onClick={onYes} disabled={mode !== 'delete' && !requiredOK}>
                {yesLabel}
            </Button>
        </div>
    );


    return (
        <ModalShell open={open} onClose={onNo} header={header} footer={footer} size={size}>
            {(mode === 'create' || mode === 'edit') && fields.length > 0 && (
                <div className={styles.grid}>
                    {fields.map((f) => {
                        const raw = coerceFormattedValue(f, values?.[f.key]);

                        // Render-time default for multi to avoid flicker while options load
                        let effectiveValue;
                        if (f.type === 'multi') {
                            const arr = Array.isArray(raw) ? raw : [];
                            if (arr.length === 0) {
                                const opts = optsMap[f.key] || [];
                                const first = firstEnabled(opts) || {};
                                effectiveValue = [{ type: first.value ?? '', value: '' }];
                            } else {
                                effectiveValue = arr;
                            }
                        } else {
                            effectiveValue = (raw === undefined) ? null : raw;
                        }

                        return (
                            <div
                                className={`${styles.field} ${f.fullRow ? styles.fieldFull : ''}`}
                                key={f.key}
                            >
                                {f.type !== 'multi' && (
                                    <label className={styles.label}>
                                        {f.label}{f.required ? '*' : ''}
                                    </label>
                                )}
                                <div className={styles.controlRow}>
                                    <Field
                                        cfg={f}
                                        value={effectiveValue}
                                        onChange={(val) => {
                                            if ((val === 'None' || val === '') && f.type !== 'multi') {
                                                setValue(f.key, null);
                                            } else {
                                                setValue(f.key, coerceParsedValue(f, val));
                                            }
                                        }}
                                        options={optsMap[f.key] || []}
                                        inputClassName={styles.input}
                                    />
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
            {mode === 'delete' && <div className={styles.subtitle}>{subtitle}</div>}
        </ModalShell>
    );
}
